/**
 * Bare-`?e2e=true` session bootstrap.
 *
 * The library's `AppAuthService.getSessionToken()` falls back to an OPAQUE
 * string (`e2e-mock-stytch-token-12345`) when no `auth_session_token` exists in
 * `localStorage`. `CompanyContextService.userCode()` decodes that token as a
 * JWT and reads its `external_id` claim — an opaque string decodes to nothing,
 * so no company resolves and (per TASK-050) every Catalogs screen redirects to
 * the dashboard. The library constant cannot be changed here, so the app seeds
 * a JWT-shaped default BEFORE the library ever reaches its own fallback.
 *
 * This is a BOOT-TIME seed only, not a session-wide guarantee: the library's own
 * mock `loginWithPassword` can later overwrite `auth_session_token` with its
 * opaque default mid-session (known residual `REVIEW-053` F1, fixed separately).
 *
 * `main.ts` calls `seedE2eSessionToken` from `prepareApp()`; this module is kept
 * free of MSW/bootstrap imports so the seeding logic is unit-testable.
 */

/** localStorage key the library's `AppAuthService` reads the session JWT from. */
const SESSION_TOKEN_KEY = 'auth_session_token';

/**
 * Claims mirrored EXACTLY from the Playwright harnesses' `SESSION_JWT`
 * (`e2e/prices.spec.ts`, `e2e/catalogs.spec.ts`, …). The UAS companies mock
 * ignores the actual value and returns its fixtures for any caller; the token
 * only needs to be JWT-decodable with an `external_id` claim so
 * `CompanyContextService` performs the company lookup instead of short-circuiting.
 */
const E2E_DEFAULT_SESSION_CLAIMS = { sub: 'member-e2e', external_id: 'user-42' } as const;

/** base64url-encodes a string (JWT payload segment), matching the e2e harnesses. */
function base64UrlEncode(value: string): string {
  return btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * JWT-shaped default session token seeded before the library falls back to its
 * own opaque `DEFAULT_E2E_TOKEN`.
 */
export const E2E_DEFAULT_SESSION_JWT = [
  'header',
  base64UrlEncode(JSON.stringify(E2E_DEFAULT_SESSION_CLAIMS)),
  'signature',
].join('.');

/**
 * Seeds a JWT-shaped `auth_session_token` for bare `?e2e=true` browsing, but
 * only when e2e mode is active and no token (from a harness or a real session)
 * is already present — an existing token is never overwritten.
 *
 * `isE2eActive` is passed in by the caller (`main.ts` computes it from
 * `isEndToEndTesting(environment.production)`) so the gate can be exercised
 * against both branches in unit tests without mutating `environment`.
 */
export function seedE2eSessionToken(isE2eActive: boolean): void {
  if (!isE2eActive) {
    return;
  }
  if (window.localStorage.getItem(SESSION_TOKEN_KEY)) {
    return;
  }
  window.localStorage.setItem(SESSION_TOKEN_KEY, E2E_DEFAULT_SESSION_JWT);
}
